import React from "react";
import {
  FiArrowUpRight,
  FiCheckCircle,
  FiCreditCard,
  FiLayers,
  FiShoppingBag,
  FiTrendingUp,
  FiTruck,
  FiZap,
  FiUsers,
} from "react-icons/fi";

import "../styles/Ecommerce.css";

const cards = [
  [
    <FiShoppingBag />,
    "Conversion-first storefronts",
    "Fast shopping experiences designed around customer confidence.",
    "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1000&q=85",
  ],
  [
    <FiCreditCard />,
    "Checkout optimisation",
    "Simpler paths to purchase that reduce friction and increase completed orders.",
    "https://images.unsplash.com/photo-1556742111-a301076d9d18?auto=format&fit=crop&w=1000&q=85",
  ],
  [
    <FiLayers />,
    "Connected commerce",
    "Integrations that keep products, inventory, fulfilment, and customer data aligned.",
    "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1000&q=85",
  ],
];

const services = [
  [
    "E - Commerce Platforms",
    "Scalable online stores built around your brand, products, customers, and growth goals.",
    <FiShoppingBag />,
  ],
  [
    "UX & Conversion",
    "Product discovery and checkout journeys engineered to make digital buying simpler.",
    <FiZap />,
  ],
  [
    "Commerce Integrations",
    "Connect payments, ERP, inventory, shipping, CRM, analytics, and marketing systems.",
    <FiLayers />,
  ],
  [
    "Experimentation",
    "Test, learn, measure, and turn performance data into actionable improvements.",
    <FiTrendingUp />,
  ],
];

const results = [
  "Faster, more confident purchasing",
  "A connected commerce ecosystem",
  "Smarter retention and repeat revenue",
  "Clearer decisions from performance data",
];

export default function Ecommerce() {
  return (
    <main className="ecommerce-page professional-page">
      {/* HERO */}
      <section className="sector-hero ecommerce-hero">
        <div className="ecommerce-hero-content">
          <div className="ecommerce-eyebrow">
            <span>Techlynx for eCommerce</span>
            <i />
            <small>01 / COMMERCE SYSTEMS</small>
          </div>

          <h1>
            Commerce experiences built to <em>convert</em>
          </h1>

          <p>
            We build high-performing eCommerce experiences that make it easier
            for customers to discover, decide, buy, and return.
          </p>

          <div className="ecommerce-actions">
            <a href="#ecommerce-solutions">
              Explore solutions <FiArrowUpRight />
            </a>

            <div className="commerce-mini-stat">
              <strong>24/7</strong>
              <span>Digital storefront</span>
            </div>
          </div>
        </div>

        {/* HERO VISUAL */}
        <div className="ecommerce-hero-visual">
          <div className="hero-orbit orbit-one" />
          <div className="hero-orbit orbit-two" />

          <div className="commerce-image-frame">
            <img
              src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1400&q=90"
              alt="Modern eCommerce shopping experience"
            />

            <div className="commerce-image-overlay" />

            <div className="hero-product-card">
              <span>LIVE COMMERCE</span>
              <strong>STORE / 01</strong>
              <small>Digital experience active</small>
            </div>
          </div>

          <div className="floating-commerce-card floating-sales">
            <span>CONVERSION</span>
            <strong>+38.4%</strong>
            <small>Experience optimisation</small>
          </div>

          <div className="hero-scan-line" />
        </div>
      </section>

      {/* CAPABILITIES */}
      <section
        id="ecommerce-solutions"
        className="sector-capabilities ecommerce-capabilities"
      >
        <header className="commerce-section-header">
          <div>
            <span>eCommerce technology</span>
            <h2>
              Every click closer to <em>conversion</em>
            </h2>
          </div>

          <p>
            We connect persuasive storefronts, frictionless checkout, and the
            systems behind them into one intelligent commerce experience.
          </p>
        </header>

        <div className="capability-grid ecommerce-capability-grid">
          {cards.map(([icon, title, text, image], index) => (
            <article
              key={title}
              className="commerce-capability-card"
              style={{ "--card-delay": `${index * 0.15}s` }}
            >
              <div className="capability-image">
                <img src={image} alt={title} loading="lazy" />
                <div className="capability-image-glow" />
                <span>0{index + 1}</span>
              </div>

              <div className="capability-icon">{icon}</div>

              <h3>{title}</h3>
              <p>{text}</p>

              <div className="card-arrow">
                <FiArrowUpRight />
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* COMMERCE JOURNEY */}
      <section className="commerce-journey">
        <div className="journey-content">
          <span>THE DIGITAL BUYING JOURNEY</span>

          <h2>
            From discovery to <em>delivery</em>
          </h2>

          <p>
            Every customer interaction becomes part of one connected commerce
            journey — from the first product impression to checkout,
            fulfilment, and repeat purchase.
          </p>

          <div className="journey-points">
            <div>
              <b>01</b>
              <span>Discover</span>
            </div>
            <div>
              <b>02</b>
              <span>Explore</span>
            </div>
            <div>
              <b>03</b>
              <span>Purchase</span>
            </div>
            <div>
              <b>04</b>
              <span>Return</span>
            </div>
          </div>
        </div>

        <div className="journey-visual">
          <div className="journey-image">
            <img
              src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1500&q=85"
              alt="Digital commerce workspace"
              loading="lazy"
            />

            <div className="journey-image-overlay" />

            <div className="journey-dashboard">
              <div className="dashboard-top">
                <span>COMMERCE FLOW</span>
                <FiTrendingUp />
              </div>

              <div className="dashboard-number">84.7%</div>

              <div className="dashboard-line">
                <i />
                <i />
                <i />
                <i />
                <i />
                <i />
              </div>

              <small>Customer journey efficiency</small>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="sector-services ecommerce-services">
        <header className="commerce-section-header">
          <div>
            <span>Where we add value</span>
            <h2>
              One connected team for your <em>next sale</em>
            </h2>
          </div>

          <p>
            Strategy, experience, engineering, integration, and optimisation
            working together around your commerce goals.
          </p>
        </header>

        <div className="service-grid ecommerce-service-grid">
          {services.map(([title, text, icon], index) => (
            <article
              key={title}
              className="commerce-service-card"
              style={{ "--service-delay": `${index * 0.12}s` }}
            >
              <div className="service-top">
                <b>0{index + 1}</b>
                <div>{icon}</div>
              </div>

              <h3>{title}</h3>

              <p>{text}</p>

              <span className="service-line" />
            </article>
          ))}
        </div>
      </section>

      {/* IMAGE / DATA SECTION */}
      <section className="commerce-intelligence">
        <div className="intelligence-image">
          <img
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1500&q=90"
            alt="eCommerce analytics and performance dashboard"
            loading="lazy"
          />

          <div className="intelligence-overlay" />

          <div className="analytics-card">
            <span>COMMERCE INTELLIGENCE</span>

            <div className="analytics-number">
              <strong>+62%</strong>
              <small>Performance growth</small>
            </div>

            <div className="analytics-bars">
              <i />
              <i />
              <i />
              <i />
              <i />
              <i />
              <i />
            </div>
          </div>
        </div>

        <div className="intelligence-content">
          <span>DATA → DECISIONS</span>

          <h2>
            Turn commerce data into <em>momentum</em>
          </h2>

          <p>
            Understand what customers are doing, identify friction, and create
            a continuous improvement cycle across your digital storefront.
          </p>

          <div className="intelligence-list">
            <div>
              <FiTrendingUp />
              <span>Performance analytics</span>
            </div>

            <div>
              <FiUsers />
              <span>Customer behaviour insights</span>
            </div>

            <div>
              <FiTruck />
              <span>Fulfilment visibility</span>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="sector-results ecommerce-results">
        <div className="results-glow" />

        <header>
          <span>Built for growth</span>

          <h2>
            Technology that keeps commerce <em>moving</em>
          </h2>

          <p>
            A stronger digital commerce foundation helps teams move faster,
            understand customers better, and continuously improve.
          </p>
        </header>

        <div className="result-points ecommerce-result-points">
          {results.map((text, index) => (
            <p key={text}>
              <span className="result-number">0{index + 1}</span>
              <FiCheckCircle />
              <span>{text}</span>
            </p>
          ))}
        </div>

        <div className="results-bottom-line">
          <span>TECHLYNX / ECOMMERCE</span>
          <b>BUILD · CONNECT · OPTIMISE</b>
        </div>
      </section>
    </main>
  );
}